import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Modal, Slider, Tooltip } from "antd";
import "./DocumentPreviewViewer.less";

const ZOOM_MIN = 50;
const ZOOM_MAX = 300;
const ZOOM_STEP = 25;

export function getPreviewKind(file) {
  if (!file) return "empty";
  const ct = (file.content_type || "").toLowerCase();
  const name = (file.original_name || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(jpg|jpeg|png|webp|gif|heic|bmp)$/.test(name)) {
    return "image";
  }
  if (ct.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv)$/.test(name)) {
    return "video";
  }
  if (ct.includes("pdf") || name.endsWith(".pdf")) {
    return "pdf";
  }
  return "other";
}

function useZoomWheel(stageRef, enabled, onWheel) {
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !enabled) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      onWheel(event);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [stageRef, enabled, onWheel]);
}

function PreviewToolbar({
  kind,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onFullscreen,
  showZoom,
  fileName,
  openUrl,
  isFullscreenMode,
}) {
  return (
    <div className="pv-preview-toolbar">
      <span className="pv-preview-toolbar-name" title={fileName}>
        {fileName || "Preview"}
      </span>
      <div className="pv-preview-toolbar-actions">
        {showZoom && (
          <>
            <Tooltip title="Zoom out">
              <Button size="small" icon="minus" onClick={onZoomOut} disabled={zoom <= ZOOM_MIN} />
            </Tooltip>
            <span className="pv-preview-zoom-label">{zoom}%</span>
            <Tooltip title="Zoom in">
              <Button size="small" icon="plus" onClick={onZoomIn} disabled={zoom >= ZOOM_MAX} />
            </Tooltip>
            <Tooltip title="Reset zoom and pan">
              <Button size="small" icon="reload" onClick={onZoomReset} />
            </Tooltip>
          </>
        )}
        {kind === "other" && openUrl && (
          <Button
            size="small"
            icon="export"
            href={openUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </Button>
        )}
        <Tooltip title={isFullscreenMode ? "Exit fullscreen" : "Fullscreen"}>
          <Button
            size="small"
            icon={isFullscreenMode ? "close" : "fullscreen"}
            onClick={onFullscreen}
          />
        </Tooltip>
      </div>
    </div>
  );
}

function PreviewContent({ file, kind, zoom, pan, isPanning, fullscreen, emptyLabel }) {
  if (!file?.preview_url) {
    return (
      <div className="pv-preview-empty">
        <Icon type="eye" />
        <span>{emptyLabel}</span>
      </div>
    );
  }

  if (kind === "image") {
    return (
      <div
        className={`pv-preview-viewport pv-preview-viewport--pannable ${
          fullscreen ? "is-fullscreen" : ""
        } ${zoom > 100 ? "can-pan" : ""} ${isPanning ? "is-panning" : ""}`}
      >
        <div
          className="pv-preview-image-wrap"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom / 100})`,
          }}
        >
          <img
            src={file.preview_url}
            alt={file.original_name || ""}
            className="pv-preview-image"
            draggable={false}
          />
        </div>
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className={`pv-preview-viewport ${fullscreen ? "is-fullscreen" : ""}`}>
        <video
          controls
          playsInline
          className="pv-preview-video"
          src={file.preview_url}
          key={file.preview_url}
        >
          Your browser does not support video playback.
        </video>
      </div>
    );
  }

  if (kind === "pdf") {
    return (
      <div className={`pv-preview-viewport ${fullscreen ? "is-fullscreen" : ""}`}>
        <iframe
          title={file.original_name || "PDF preview"}
          src={file.preview_url}
          className="pv-preview-pdf"
        />
      </div>
    );
  }

  return (
    <div className="pv-preview-empty">
      <Icon type="file" />
      <a href={file.preview_url} target="_blank" rel="noopener noreferrer">
        Open file in new tab
      </a>
    </div>
  );
}

function PreviewStage({
  stageRef,
  kind,
  zoom,
  isPanning,
  onPanStart,
  children,
  fullscreen,
}) {
  const isImage = kind === "image";
  const canPan = isImage && zoom > 100;

  const handleMouseDown = useCallback(
    (event) => {
      if (!canPan || event.button !== 0) return;
      event.preventDefault();
      onPanStart(event.clientX, event.clientY);
    },
    [canPan, onPanStart]
  );

  const handleTouchStart = useCallback(
    (event) => {
      if (!canPan || event.touches.length !== 1) return;
      onPanStart(event.touches[0].clientX, event.touches[0].clientY);
    },
    [canPan, onPanStart]
  );

  return (
    <div
      ref={stageRef}
      className={`pv-preview-stage ${isImage ? "pv-preview-stage--image" : ""} ${
        fullscreen ? "is-fullscreen-stage" : ""
      } ${canPan ? "can-pan" : ""} ${isPanning ? "is-panning" : ""}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {children}
    </div>
  );
}

const DocumentPreviewViewer = ({
  file,
  className,
  emptyLabel = "Select a partner file to preview",
  compact = false,
}) => {
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const inlineStageRef = useRef(null);
  const fullscreenStageRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const panSessionRef = useRef(null);

  const kind = useMemo(() => getPreviewKind(file), [file]);

  const resetView = useCallback(() => {
    const origin = { x: 0, y: 0 };
    panRef.current = origin;
    setPan(origin);
    setZoom(100);
    setIsPanning(false);
    panSessionRef.current = null;
  }, []);

  useEffect(() => {
    resetView();
    setFullscreenOpen(false);
  }, [file?.id, file?.preview_url, resetView]);

  useEffect(() => {
    if (zoom <= 100) {
      const origin = { x: 0, y: 0 };
      panRef.current = origin;
      setPan(origin);
    }
  }, [zoom]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  const releasePageScroll = useCallback(() => {
    const openModalWraps = document.querySelectorAll(
      ".ant-modal-wrap:not(.ant-modal-wrap-hidden)"
    );
    if (openModalWraps.length > 0) {
      return;
    }
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.classList.remove("ant-scrolling-effect");
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  }, []);

  const handleZoomReset = useCallback(() => {
    resetView();
  }, [resetView]);

  const handleWheel = useCallback(
    (event) => {
      if (kind !== "image") return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom((z) => Math.min(Math.max(z + delta, ZOOM_MIN), ZOOM_MAX));
    },
    [kind]
  );

  const handlePanStart = useCallback(
    (clientX, clientY) => {
      if (kind !== "image" || zoom <= 100) return;
      panSessionRef.current = {
        startX: clientX,
        startY: clientY,
        originPan: { ...panRef.current },
      };
      setIsPanning(true);
    },
    [kind, zoom]
  );

  const handlePanMove = useCallback((clientX, clientY) => {
    const session = panSessionRef.current;
    if (!session) return;
    const nextPan = {
      x: session.originPan.x + (clientX - session.startX),
      y: session.originPan.y + (clientY - session.startY),
    };
    panRef.current = nextPan;
    setPan(nextPan);
  }, []);

  const handlePanEnd = useCallback(() => {
    panSessionRef.current = null;
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (!isPanning) return undefined;

    const onMouseMove = (event) => {
      event.preventDefault();
      handlePanMove(event.clientX, event.clientY);
    };
    const onMouseUp = () => handlePanEnd();
    const onTouchMove = (event) => {
      if (event.touches.length !== 1) return;
      event.preventDefault();
      handlePanMove(event.touches[0].clientX, event.touches[0].clientY);
    };
    const onTouchEnd = () => handlePanEnd();

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isPanning, handlePanMove, handlePanEnd]);

  const showZoom = kind === "image";

  useZoomWheel(inlineStageRef, showZoom && !fullscreenOpen, handleWheel);
  useZoomWheel(fullscreenStageRef, showZoom && fullscreenOpen, handleWheel);

  const previewProps = {
    file,
    kind,
    zoom,
    pan,
    isPanning,
    emptyLabel,
  };

  return (
    <>
      <div
        className={`pv-preview-shell ${compact ? "pv-preview-shell--compact" : ""} ${
          className || ""
        }`}
      >
        <PreviewToolbar
          kind={kind}
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onZoomReset={handleZoomReset}
          onFullscreen={() => setFullscreenOpen(true)}
          showZoom={showZoom}
          fileName={file?.original_name}
          openUrl={file?.preview_url}
          isFullscreenMode={false}
        />
        <PreviewStage
          stageRef={inlineStageRef}
          kind={kind}
          zoom={zoom}
          isPanning={isPanning}
          onPanStart={handlePanStart}
        >
          <PreviewContent {...previewProps} fullscreen={false} />
        </PreviewStage>
        {showZoom && (
          <div className="pv-preview-slider">
            <Slider
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={ZOOM_STEP}
              value={zoom}
              onChange={setZoom}
              tipFormatter={(v) => `${v}%`}
            />
          </div>
        )}
      </div>

      <Modal
        visible={fullscreenOpen}
        onCancel={() => setFullscreenOpen(false)}
        afterClose={releasePageScroll}
        footer={null}
        width="100%"
        className="pv-preview-fullscreen-modal"
        wrapClassName="pv-preview-fullscreen-wrap"
        getContainer={() => document.body}
        zIndex={2000}
        destroyOnClose
        centered={false}
        style={{ top: 0, paddingBottom: 0, maxWidth: "100vw" }}
        bodyStyle={{ padding: 0, overflow: "hidden", height: "calc(100vh - 55px)" }}
        title={file?.original_name || "Document preview"}
      >
        <div className="pv-preview-fullscreen-inner">
          <PreviewToolbar
            kind={kind}
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onFullscreen={() => setFullscreenOpen(false)}
            showZoom={showZoom}
            fileName={file?.original_name}
            openUrl={file?.preview_url}
            isFullscreenMode
          />
          <PreviewStage
            stageRef={fullscreenStageRef}
            kind={kind}
            zoom={zoom}
            isPanning={isPanning}
            onPanStart={handlePanStart}
            fullscreen
          >
            <PreviewContent {...previewProps} fullscreen />
          </PreviewStage>
          {showZoom && (
            <div className="pv-preview-slider">
              <Slider
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={ZOOM_STEP}
                value={zoom}
                onChange={setZoom}
                tipFormatter={(v) => `${v}%`}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DocumentPreviewViewer;
